import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const initiateOAuth = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/oauth/authorize`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Failed to initiate OAuth connection');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-gray-800 mb-4">
              The Report Ninja
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Powerful reporting and analytics for your GoHighLevel subaccounts
            </p>
          </div>

          {/* Hero Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/2">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Connect Your GoHighLevel Account
                </h2>
                <p className="text-gray-600 mb-6">
                  Seamlessly integrate with your GoHighLevel subaccounts to access 
                  comprehensive reports on contacts, calendars, and more.
                </p>
                
                <button
                  onClick={initiateOAuth}
                  disabled={isLoading}
                  className={`px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 ${
                    isLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'hover:opacity-90 shadow-lg'
                  }`}
                  style={!isLoading ? {background: 'linear-gradient(to right, #d4161d, #52aef2)'} : {}}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Connecting...</span>
                    </div>
                  ) : (
                    'Connect to GoHighLevel'
                  )}
                </button>
                
                {error && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
              </div>
              
              <div className="lg:w-1/2">
                <div className="rounded-xl p-6 text-white" style={{background: 'linear-gradient(135deg, #d4161d, #52aef2)'}}>
                  <h3 className="text-xl font-semibold mb-4">What you'll get:</h3>
                  <ul className="space-y-2 text-left">
                    <li className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{color: '#f9d548'}} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Real-time contact analytics</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{color: '#f9d548'}} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Calendar and appointment insights</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{color: '#f9d548'}} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Detailed location reports</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{color: '#f9d548'}} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Easy embedding in GHL menu</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#52aef2', opacity: '0.1'}}>
                <svg className="w-6 h-6" style={{color: '#52aef2'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Advanced Analytics</h3>
              <p className="text-gray-600">Get deep insights into your subaccount performance with detailed analytics and visualizations.</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#f9d548', opacity: '0.1'}}>
                <svg className="w-6 h-6" style={{color: '#f9d548'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Real-time Data</h3>
              <p className="text-gray-600">Access live data from your GoHighLevel subaccounts with automatic synchronization.</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#d4161d', opacity: '0.1'}}>
                <svg className="w-6 h-6" style={{color: '#d4161d'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Easy Integration</h3>
              <p className="text-gray-600">Embed reports directly into your GoHighLevel interface with custom menu links.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;